/**
 * Decision Log Template Form
 * Project decision tracking
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const DecisionLogForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '', date: new Date().toISOString().split('T')[0], prepared_by: '',
    decisions: [{ id: '', date: '', title: '', description: '', options_considered: '', decision_made: '', rationale: '', decision_maker: '', stakeholders: '', impact: '', status: 'Pending', review_date: '', notes: '' }],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    project_name: 'E-Commerce Platform v2.0',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'Project Manager',
    decisions: [
      { id: 'DEC-001', date: '2025-01-05', title: 'Cloud Provider Selection', description: 'Choose cloud provider for platform hosting', options_considered: '1. AWS\n2. Google Cloud\n3. Azure', decision_made: 'AWS', rationale: 'Team has most experience with AWS, better pricing for our use case, stronger container services', decision_maker: 'CTO', stakeholders: 'Engineering Team, Finance, Operations', impact: 'High - affects all infrastructure decisions', status: 'Approved', review_date: '2025-07-01', notes: 'Review in 6 months' },
      { id: 'DEC-002', date: '2025-01-08', title: 'Frontend Framework', description: 'Select frontend framework for new UI', options_considered: '1. React\n2. Vue.js\n3. Angular', decision_made: 'React', rationale: 'Larger talent pool, existing team expertise, better ecosystem for e-commerce', decision_maker: 'Tech Lead', stakeholders: 'Frontend Team, UX Team', impact: 'Medium - affects frontend development', status: 'Approved', review_date: '', notes: '' },
      { id: 'DEC-003', date: '2025-01-10', title: 'Payment Gateway', description: 'Choose primary payment processor', options_considered: '1. Stripe\n2. PayPal\n3. Adyen', decision_made: 'Stripe + PayPal', rationale: 'Stripe for cards (best API), PayPal for checkout option (customer demand)', decision_maker: 'Product Manager', stakeholders: 'Finance, Legal, Engineering', impact: 'High - affects checkout flow and revenue', status: 'Approved', review_date: '', notes: 'PayPal as secondary option' },
      { id: 'DEC-004', date: '2025-01-12', title: 'Database Migration Strategy', description: 'Approach for PostgreSQL upgrade', options_considered: '1. Big bang migration\n2. Blue-green deployment\n3. Gradual migration with proxy', decision_made: 'Blue-green deployment', rationale: 'Allows instant rollback, minimal downtime, team has experience', decision_maker: 'DBA Lead', stakeholders: 'Engineering, Operations, QA', impact: 'Medium - weekend maintenance window needed', status: 'Pending', review_date: '2025-01-20', notes: 'Awaiting capacity planning review' },
      { id: 'DEC-005', date: '2025-01-15', title: 'Launch Date', description: 'Target date for v2.0 production launch', options_considered: '1. March 15, 2025\n2. April 1, 2025\n3. Q2 2025', decision_made: 'April 1, 2025', rationale: 'Allows buffer for testing, avoids March fiscal close, aligns with marketing campaign', decision_maker: 'Steering Committee', stakeholders: 'All departments', impact: 'High - affects all project timelines', status: 'Approved', review_date: '2025-02-15', notes: 'Monthly checkpoint reviews' }
    ],
    rai_assignments: [
      { task: 'Document all architecture decisions', responsible: 'Tech Lead', accountable: 'CTO', informed: 'Engineering Team', due_date: '2025-01-31', status: 'In Progress' }
    ],
    followups: [
      { item: 'Review database migration decision', owner: 'DBA Lead', due_date: '2025-01-20', priority: 'High', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Decision Log Draft";
      const payload = {
        name: draftName,
        template_type: "Decision Log",
        template_id: "decision-log",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "decision-log", "Decision Log", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("decision-log", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/decision-log`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Decision_Log_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Decision Log",
        formData,
        settings: activeSettings || {},
        templateType: "Decision Log",
        templateId: "decision-log",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addDecision = () => {
    const newId = `DEC-${String(formData.decisions.length + 1).padStart(3, '0')}`;
    setFormData({
      ...formData,
      decisions: [...formData.decisions, { id: newId, date: new Date().toISOString().split('T')[0], title: '', description: '', options_considered: '', decision_made: '', rationale: '', decision_maker: '', stakeholders: '', impact: '', status: 'Pending', review_date: '', notes: '' }]
    });
  };

  const updateDecision = (index, field, value) => {
    const decisions = [...formData.decisions];
    decisions[index] = { ...decisions[index], [field]: value };
    setFormData({ ...formData, decisions });
  };

  const removeDecision = (index) => {
    setFormData({ ...formData, decisions: formData.decisions.filter((_, i) => i !== index) });
  };

  const statuses = ['Pending', 'Under Review', 'Approved', 'Rejected', 'Superseded', 'Deferred'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Decision Log</h1><p className="text-sm text-gray-500">Project decision tracking</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Decision Log</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Project name" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Name" /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Decisions ({formData.decisions.length})</Label>
              <div className="space-y-4 mt-3">
                {formData.decisions.map((dec, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${dec.status === 'Approved' ? 'bg-green-50' : dec.status === 'Rejected' ? 'bg-red-50' : 'bg-gray-50'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono bg-white px-2 py-1 rounded">{dec.id}</span>
                        <span className={`text-xs px-2 py-1 rounded ${dec.status === 'Approved' ? 'bg-green-200 text-green-800' : dec.status === 'Pending' ? 'bg-yellow-200 text-yellow-800' : 'bg-gray-200'}`}>{dec.status}</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeDecision(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2"><Label className="text-xs">Decision Title *</Label><Input value={dec.title} onChange={(e) => updateDecision(idx, 'title', e.target.value)} placeholder="What was decided" className="text-sm" /></div>
                      <div><Label className="text-xs">Date</Label><Input type="date" value={dec.date} onChange={(e) => updateDecision(idx, 'date', e.target.value)} className="text-sm" /></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <div><Label className="text-xs">Options Considered</Label><Textarea value={dec.options_considered} onChange={(e) => updateDecision(idx, 'options_considered', e.target.value)} placeholder="1. Option A\n2. Option B" rows={3} className="text-sm" /></div>
                      <div><Label className="text-xs">Rationale</Label><Textarea value={dec.rationale} onChange={(e) => updateDecision(idx, 'rationale', e.target.value)} placeholder="Why this decision was made" rows={3} className="text-sm" /></div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                      <div><Label className="text-xs">Decision Made</Label><Input value={dec.decision_made} onChange={(e) => updateDecision(idx, 'decision_made', e.target.value)} placeholder="Final decision" className="text-sm" /></div>
                      <div><Label className="text-xs">Decision Maker</Label><Input value={dec.decision_maker} onChange={(e) => updateDecision(idx, 'decision_maker', e.target.value)} placeholder="Who decided" className="text-sm" /></div>
                      <div><Label className="text-xs">Status</Label>
                        <Select value={dec.status} onValueChange={(v) => updateDecision(idx, 'status', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Review Date</Label><Input type="date" value={dec.review_date} onChange={(e) => updateDecision(idx, 'review_date', e.target.value)} className="text-sm" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addDecision} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Decision</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default DecisionLogForm;
